import React, { useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg';
import ColorModel from './models/ColorModel';
import ColorModal from './ColorModal';
import { getStorageValues } from '../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { logAuditEvent } from '../../utils/auditLogger';

function ColorGrid({ ColorData, fetchColors, UserPermissions = {}, moduleName = 'Color' }) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [colorModel, setColorModel] = useState(new ColorModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);

  const closeModal = () => {
    setIsOpen(false);
    fetchColors();
  };

  const rows = Array.isArray(ColorData)
    ? ColorData.filter((item) => {
        const search = filterText.toLowerCase();
        return (
          (item.ColorName && item.ColorName.toLowerCase().includes(search)) ||
          (item.ColorCode && item.ColorCode.toLowerCase().includes(search)) ||
          (item.Description && item.Description.toLowerCase().includes(search))
        );
      })
    : [];

  const openActionType = (row, name) => {
    const detail = row?.ColorName ? `Color: ${row.ColorName}` : '';
    logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
    setColorModel(row || new ColorModel());
    setIsOpen(true);
    setActionType(name);
  };

  const validation = (colorDetails) => {
    if (!colorDetails || !colorDetails.ColorName || !colorDetails.ColorName.trim()) {
      CreateAlert('Please enter color name!!', 'Warning', setAlert);
      return false;
    }
    return true;
  };

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = {
        ColorId: data.ColorId,
        ColorName: data.ColorName,
        ColorCode: data.ColorCode,
        Description: data.Description,
        UserId: getStorageValues('userId'),
      };
      if (!validation(payload)) {
        return;
      }
      try {
        const response = await axios.post(`${getApiBaseUrl()}/saveColorDetails`, payload);
        if (response.status === 200) {
          const responseData = response.data?.data;
          const result = Array.isArray(responseData) ? responseData[0] : responseData;
          if (result?.success === 1) {
            CreateAlert(result?.message || 'Color updated successfully!!', 'Success', setAlert);
            fetchColors();
            closeModal();
          } else {
            CreateAlert(result?.message || 'Color already exists!!', 'Warning', setAlert);
          }
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update color!!', 'Error', setAlert);
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/deletecolor`, {
          ColorId: colorModel.ColorId,
          UserId: getStorageValues('userId'),
        });
        if (response.status === 200) {
          CreateAlert('Color deleted successfully!!', 'Success', setAlert);
          fetchColors();
          closeModal();
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete color!!', 'Error', setAlert);
      }
    }
  };

  const columns = [
    {
      name: (
        <div className="titlename">
          <span>Color Name</span>
        </div>
      ),
      selector: (row) => row.ColorName,
      cell: (row) => <span title={row.ColorName}>{row.ColorName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Color Code</span>
        </div>
      ),
      selector: (row) => row.ColorCode,
      cell: (row) => <span title={row.ColorCode}>{row.ColorCode || '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Description</span>
        </div>
      ),
      selector: (row) => row.Description,
      cell: (row) => <span title={row.Description}>{row.Description}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
  ];

  if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {UserPermissions.ViewModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'View')}>
              <Icons.Eye className="action-eye" />
            </Link>
          ) : null}
          {UserPermissions.EditModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'Edit')}>
              <Icons.Edit className="feather-edit" />
            </Link>
          ) : null}
          {UserPermissions.DeleteModule === 1 ? (
            <Link className="confirm-text p-2" to="" onClick={() => openActionType(row, 'Delete')}>
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          ) : null}
        </div>
      ),
      center: 'true',
      width: '150px',
    });
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="ColorName"
            sortIcon={<SortArrow />}
            persistTableHead
          />
        </div>
      </div>
      <ColorModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={colorModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ColorGrid;
